import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import { 
  getIssueLabel, 
  getPriorityLabel, 
  getStatusLabel, 
  getStatusColor, 
  getPriorityColor 
} from '@/utils/reportLabels';
import type { Marker } from '@/types';
import api from '@/services/api';

const MarkerDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [marker, setMarker] = useState<Marker | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (id) {
      fetchMarker();
    }
  }, [id]);

  const fetchMarker = async () => {
    if (!id) return;

    try {
      setLoading(true);
      const response = await api.get(`/markers/${id}`);

      if (response.data.success) {
        setMarker(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching marker:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyMarker = async () => {
    if (!marker) return;

    try {
      const response = await api.post(`/markers/${marker._id}/verify`);

      if (response.data.success) {
        setMarker(prev => prev ? {
          ...prev,
          metadata: { ...prev.metadata, verified: true }
        } : null);
      }
    } catch (error) {
      console.error('Error verifying marker:', error);
    }
  };

  const handleDeleteMarker = async () => {
    if (!marker || !confirm('Sei sicuro di voler eliminare questa segnalazione?')) {
      return;
    }

    try {
      await api.delete(`/markers/${marker._id}`);
      navigate('/admin/markers');
    } catch (error) {
      console.error('Error deleting marker:', error);
    }
  };

  const handleUpdateReportStatus = async (reportId: string, status: string) => {
    if (!marker) return;

    try {
      const response = await api.put(`/markers/${marker._id}/reports/${reportId}`, {
        status
      });

      if (response.data.success) {
        setMarker(response.data.data);
      }
    } catch (error) {
      console.error('Error updating report status:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!marker) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Segnalazione non trovata</h2>
          <Link
            to="/admin/markers"
            className="text-blue-600 hover:text-blue-700"
          >
            Torna alle segnalazioni
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="mb-6">
          <Link
            to="/admin/markers"
            className="text-blue-600 hover:text-blue-700 text-sm font-medium"
          >
            ← Torna alle segnalazioni
          </Link>
        </div>

        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          {/* Header */}
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
            <div className="flex justify-between items-start">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">
                  {marker.streetName}
                </h1>
                {marker.streetNameCorrection && (
                  <p className="text-gray-600">
                    <strong>Nome corretto:</strong> {marker.streetNameCorrection}
                  </p>
                )}
                <div className="flex items-center space-x-4 mt-2">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${marker.metadata.verified
                      ? 'bg-green-100 text-green-800'
                      : 'bg-yellow-100 text-yellow-800'
                    }`}>
                    {marker.metadata.verified ? 'Verificato' : 'Da Verificare'}
                  </span>

                  <span className="text-sm text-gray-500">
                    Creata il {new Date(marker.createdAt).toLocaleDateString('it-IT')}
                  </span>
                </div>
              </div>

              <div className="flex space-x-2">
                <Link
                  to={`/admin/markers/edit/${marker._id}`}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded text-sm font-medium transition-colors"
                >
                  Modifica
                </Link>

                {!marker.metadata.verified && (
                  <button
                    onClick={handleVerifyMarker}
                    className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded text-sm font-medium transition-colors"
                  >
                    Verifica
                  </button>
                )}

                <button
                  onClick={handleDeleteMarker}
                  className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded text-sm font-medium transition-colors"
                >
                  Elimina
                </button>
              </div>
            </div>
          </div>

          <div className="p-6">
            {/* Location Info */}
            <div className="mb-8">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Informazioni Posizione</h2>
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Coordinate</p>
                    <p className="font-medium">
                      {marker.coordinates.latitude.toFixed(6)}, {marker.coordinates.longitude.toFixed(6)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Via</p>
                    <p className="font-medium">{marker.streetName}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Connection Info */}
            <div className="mb-8">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Informazioni Connessione</h2>
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Operatore</p>
                    <p className="font-medium">{marker.currentLine.operator || 'Non specificato'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Tecnologia</p>
                    <p className="font-medium">{marker.currentLine.technology}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Velocità Download</p>
                    <p className="font-medium">
                      {marker.currentLine.maxSpeed.download} {marker.currentLine.maxSpeed.unit}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Velocità Upload</p>
                    <p className="font-medium">
                      {marker.currentLine.maxSpeed.upload} {marker.currentLine.maxSpeed.unit}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Stato Linea</p>
                    <p className="font-medium">
                      {marker.currentLine.isActivated ? 'Attiva' : 'Non Attiva'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Reports */}
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Segnalazioni ({marker.reports.length})
              </h2>

              {marker.reports.length === 0 ? (
                <p className="text-gray-500">Nessuna segnalazione presente.</p>
              ) : (
                <div className="space-y-4">
                  {marker.reports.map((report) => (
                    <div key={report._id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex items-center space-x-3">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(report.status)}`}>
                            {getStatusLabel(report.status)}
                          </span>

                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPriorityColor(report.priority)}`}>
                            {getPriorityLabel(report.priority)}
                          </span>
                        </div>

                        <div className="text-sm text-gray-500">
                          {new Date(report.reportedAt).toLocaleDateString('it-IT')}
                        </div>
                      </div>

                      <div className="mb-3">
                        <p className="text-sm text-gray-600 mb-1">
                          <strong>Problema:</strong> {getIssueLabel(report.issue)}
                        </p>

                        {report.description && (
                          <p className="text-sm text-gray-700">{report.description}</p>
                        )}
                      </div>

                      {report.reportedBy && (
                        <div className="mb-3 text-sm">
                          <p className="text-gray-600">
                            <strong>Segnalato da:</strong>{' '}
                            {report.reportedBy.username && `${report.reportedBy.username} `}
                            {report.reportedBy.name && `${report.reportedBy.name} `}
                            {report.reportedBy.email && `(${report.reportedBy.email})`}
                            {report.reportedBy.phone && ` - ${report.reportedBy.phone}`}
                          </p>
                        </div>
                      )}

                      <div className="flex justify-between items-center">
                        <div className="text-sm text-gray-500">
                          👥 {report.supportCount || 1} persona/e ha segnalato questo problema
                        </div>

                        <div className="flex space-x-2">
                          {report.status === 'open' && (
                            <button
                              onClick={() => handleUpdateReportStatus(report._id, 'in_progress')}
                              className="text-xs bg-yellow-600 hover:bg-yellow-700 text-white px-2 py-1 rounded transition-colors"
                            >
                              In Corso
                            </button>
                          )}

                          {(report.status === 'open' || report.status === 'in_progress') && (
                            <button
                              onClick={() => handleUpdateReportStatus(report._id, 'resolved')}
                              className="text-xs bg-green-600 hover:bg-green-700 text-white px-2 py-1 rounded transition-colors"
                            >
                              Risolvi
                            </button>
                          )}

                          {report.status !== 'closed' && (
                            <button
                              onClick={() => handleUpdateReportStatus(report._id, 'closed')}
                              className="text-xs bg-gray-600 hover:bg-gray-700 text-white px-2 py-1 rounded transition-colors"
                            >
                              Chiudi
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarkerDetail;